import './Wallets.css';
import { rarityNameFor, getMagmicCoinBalanceString, imageURLForAsset } from './../../shared/assetHelpers';
import imageMagmicCoin from './../../images/magmic_coin.png';
import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import ReactGA from "react-ga4";
import { Auth } from 'aws-amplify';
import { AmplifyProvider, Authenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';

import { ModalContainer } from '../../shared/components/modalContainer/ModalContainer';
import { HeaderActivityIndicator } from '../../shared/components/activityIndicator/ActivityIndicator';
import { MarketplaceSellForm, MarketplaceDelistForm } from '../../shared/components/transactions/Transactions';
import Pet from '../../shared/components/pet/Pet';
import { IdlePetParadiseForm } from '../../shared/components/user/User';
import { useSelector, useDispatch } from 'react-redux';

import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning'; // ACCOUNT_LINK_STATUS_TYPES.INCOMPLETE
import PendingIcon from '@mui/icons-material/Pending'; // ACCOUNT_LINK_STATUS_TYPES.PENDING
import CheckIcon from '@mui/icons-material/Check'; // ACCOUNT_LINK_STATUS_TYPES.COMPLETE
import DownloadingIcon from '@mui/icons-material/Downloading'; // ACCOUNT_LINK_STATUS_TYPES.LOADING
import BlockIcon from '@mui/icons-material/Block';  // ACCOUNT_LINK_STATUS_TYPES.INCOMPLETE_PLUS_DISABLED

import { setDataRefreshRequestedDate } from './../../redux/ducks/user';

import { amplifyTheme } from '../../shared/components/user/User';

const ACCOUNT_LINK_STATUS_TYPES = {
  LOADING: "LOADING",
  INCOMPLETE_PLUS_DISABLED: "INCOMPLETE_PLUS_DISABLED",
  INCOMPLETE: "INCOMPLETE",
  PENDING: "PENDING",
  COMPLETE: "COMPLETE"
}

const FORWARDED_ACTIONS = { // via url param defined in App.js Router Switch case
  LINK_FORTE_ACCOUNT: "fortelink",
  BUY_MAGIC_COIN: "buycoin"
}

const Wallets = () => {

  const [loading, setLocalLoading] = useState(true);
  const [forteLinkUrl, setForteLinkUrl] = useState(undefined);
  const [forwardedAction, setForwardedAction] = useState(); // using state so that forwardedActions can evolve or be cleared
  const [activeModals, setActiveModals] = useState({}); // add modals as a key/value, where the key is a unique id used to dismiss the modal and the value is what to render

  const dispatch = useDispatch();
  
  const userReduxStore = useSelector(state => {
    return state.user;
  });

  const history = useHistory();

  const goTo = (url) => {
    history.push(url);
  }

  const signOut = async () => {
    try {
      await Auth.signOut();
      goTo("/");
    } catch (error) {
      console.log('error signing out: ', error);
    }
  }

  const loadWalletAssets = () => {

    const bcConnectedUserId = userReduxStore.user && userReduxStore.user.bcConnectedUserId ? userReduxStore.user.bcConnectedUserId : undefined;
    const bcConnectedUserPassword = userReduxStore.user && userReduxStore.user.bcConnectedUserPassword ? userReduxStore.user.bcConnectedUserPassword : undefined;

    if (bcConnectedUserId && bcConnectedUserPassword) {
      if (!userReduxStore.forteData.exchangeLinkOrderId) {
        const requestOptions = {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: bcConnectedUserId,
            password: bcConnectedUserPassword
          })
        };

        let apiEndpointURL = "";
        switch (process.env.REACT_APP_ENV) {
          case "staging":
            apiEndpointURL = "https://cf35m63tng.execute-api.us-east-1.amazonaws.com/test/user/forte/getlinkurl";
            break;
          default:
            apiEndpointURL = "https://q645yqsbml.execute-api.us-east-1.amazonaws.com/prod/user/forte/getlinkurl";
            break;
        }
    
        // get the forte link url
        fetch(apiEndpointURL, requestOptions)
        .then(linkRequestResponse => linkRequestResponse.json())
        .then(forteLinkData => {
          if (!forteLinkData.response) {
            return;
          }

          if (forteLinkData.response.linkUrl) {
            setForteLinkUrl(forteLinkData.response.linkUrl);
          }
          /*
          if (forteLinkData.response.orderId) {
            setForteOrderId(forteLinkData.response.orderId);
          }*/
        });
      }
    }
  };

  // request a refresh on load
  useEffect(
    () => {
      dispatch(setDataRefreshRequestedDate(new Date())); // trigger an App.useEffect user refresh
    },
    []
  )

  const { forwardedAction: forwardedActionFromURL } = useParams();

  useEffect(
    () => {
      
      if (forwardedActionFromURL && forwardedActionFromURL !== forwardedAction) {
        setForwardedAction(forwardedActionFromURL);
        return;
      }

      if (forwardedAction) {
        switch (forwardedAction) {
          case FORWARDED_ACTIONS.LINK_FORTE_ACCOUNT:
            promptForForwardedActionForteLink("Connect To Forte Exchange");
            break;
          case FORWARDED_ACTIONS.BUY_MAGIC_COIN:
            promptForForwardedActionBuyCoin(forteLinkUrl);
            break;    
          default:
            console.warn("/wallet unrecognized forwardedAction: " + forwardedAction);
            break;
        }
      }
    },
    [forwardedAction, forteLinkUrl] // also run on history pushes and account updates (linkage changes, for example)
  );

  useEffect(
    () => {
      setLocalLoading(!userReduxStore.loadComplete); // user has loaded, assets ready
      if (userReduxStore.loadComplete) {
        loadWalletAssets();
      }
    },
    [userReduxStore.loadComplete] // run on redux user state updates
  );

  const promptForForwardedActionForteLink = () => {

    const modalId = "walletsPromptForForwardedActionForteLink";

    const activeModalsToUpdate = {}; // setting to empty object means reset all modals for this component

    const forteLinkStatus = getForteLinkStatus();

    const modalTitle = "Connect To Forte Exchange";

    const onDismiss = () => {
      const activeModalsToEdit = activeModals;
      delete activeModalsToEdit[modalId];
      setActiveModals({...activeModalsToEdit}); // use spread operator to create a new object
      history.goBack();
    };

    switch (forteLinkStatus) {

      case ACCOUNT_LINK_STATUS_TYPES.INCOMPLETE_PLUS_DISABLED:

        activeModalsToUpdate[modalId] = <ModalContainer
          title={modalTitle}
          smallPromptFormat={true}
          hideHeaderDismissButton={true}
          onDismiss={onDismiss}
        >
          <div className="marketplaceModal">
            <p>
              In order to make purchases, you need to connect your account to Forte Exchange.
            </p>
            <p>
              Before you can link a Forte Exchange account, you need to connect your game account in Idle Pet Paradise.
            </p>
            <div className="actions">
              <button className="cancel" onClick={onDismiss}>Cancel</button>
              <button className="buy" onClick={() => {
                promptToConnectIdlePetParadise();
              }}>Link Idle Pet Paradise</button>
            </div>
          </div>
        </ModalContainer>
        setActiveModals({...activeModalsToUpdate});
        return;

      case ACCOUNT_LINK_STATUS_TYPES.INCOMPLETE:

        activeModalsToUpdate[modalId] = <ModalContainer
          title={modalTitle}
          smallPromptFormat={true}
          hideHeaderDismissButton={true}
          onDismiss={onDismiss}
        >
          <div className="marketplaceModal">
            <p>
              In order to make purchases, you need to connect your account to Forte Exchange.
            </p>
            <div className="actions">
              <button className="cancel" onClick={onDismiss}>Cancel</button>
              {forteLinkUrl ?
                <a href={forteLinkUrl} target="_blank">Connect To Forte Exchange</a>
              :
                <span>Loading link ...</span>
              }
            </div>
          </div>
        </ModalContainer>
        setActiveModals({...activeModalsToUpdate});
        return;

      case ACCOUNT_LINK_STATUS_TYPES.COMPLETE:
        onDismiss();
        break;
      default:
        return;
    }
  };

  const promptForForwardedActionBuyCoin = (buyCoinsLink) => {

    const modalId = "walletsPromptForForwardedActionBuyCoin";

    const activeModalsToUpdate = activeModals;

    const onDismiss = () => {
      const activeModalsToEdit = activeModals;
      delete activeModalsToEdit[modalId];
      setActiveModals({...activeModalsToEdit}); // use spread operator to create a new object
      history.goBack();
    };

    activeModalsToUpdate[modalId] = <ModalContainer
      title={"Buy Magmic Coin"}
      smallPromptFormat={true}
      hideHeaderDismissButton={!buyCoinsLink}
      onDismiss={onDismiss}
    >
      {!buyCoinsLink ?
        <div className="marketplaceModal">
          <HeaderActivityIndicator isLoading={true} />
          <p>
            Getting your Forte Exchange link ...
          </p>
        </div>
      :
        <div className="marketplaceModal">
          <p>
            Click below to go to Forte Exchange .
          </p>
          <div className="actions">
            <a href={forteLinkUrl} target="_blank" onClick={() => {
                if (!window.location.href.startsWith("http://localhost")) {
                  ReactGA.event("purchase_magmic_coin");
                }
              }}>Buy Magmic Coin</a>
          </div>
        </div>

      }
    </ModalContainer>

    setActiveModals({...activeModalsToUpdate});
  };

  const promptToConnectIdlePetParadise = () => {

    const modalId = "walletsPromptToConnectIdlePetParadise";

    const activeModalsToUpdate = activeModals;

    const onDismiss = () => {
      const activeModalsToEdit = activeModals;
      delete activeModalsToEdit[modalId];
      setActiveModals({...activeModalsToEdit}); // use spread operator to create a new object
    };

    const onSuccess = () => {
      onDismiss();
    };

    activeModalsToUpdate[modalId] = <ModalContainer
      title={"Link to Idle Pet Paradise"}
      smallPromptFormat={true}
      hideHeaderDismissButton={false}
      onDismiss={onDismiss}
    >
      <IdlePetParadiseForm
        onDismiss={onDismiss}
        onSuccess={onSuccess}
      />
    </ModalContainer>
    setActiveModals({...activeModalsToUpdate});

  }

  const promptStarterPetsCannotBeSold = (asset) => {
    const modalId = "walletsPromptToConnectIdlePetParadise";

    const activeModalsToUpdate = activeModals;

    const onDismiss = () => {
      const activeModalsToEdit = activeModals;
      delete activeModalsToEdit[modalId];
      setActiveModals({...activeModalsToEdit}); // use spread operator to create a new object
    };

    activeModalsToUpdate[modalId] = <ModalContainer
    title={"Cannot Sell " + asset.name}
      smallPromptFormat={true}
      hideHeaderDismissButton={false}
      onDismiss={onDismiss}
    >
      Starter pets can not be sold in the Marketplace. Play Idle Pet Paradise to level up and Evolve the pets.
    </ModalContainer>
    setActiveModals({...activeModalsToUpdate});

  };
  const promptToSell = (asset) => {

    const modalId = "walletsPromptToSell";

    const activeModalsToUpdate = activeModals;

    const onDismiss = () => {
      const activeModalsToEdit = activeModals;
      delete activeModalsToEdit[modalId];
      setActiveModals({...activeModalsToEdit}); // use spread operator to create a new object
    };

    const onSuccess = () => {
      dispatch(setDataRefreshRequestedDate(new Date())); // trigger an App.useEffect user refresh
      onDismiss();
    };

    activeModalsToUpdate[modalId] = <ModalContainer
      title={"Sell " + asset.name}
      smallPromptFormat={true}
      hideHeaderDismissButton={true}
      onDismiss={onDismiss}
    >
      <MarketplaceSellForm
        asset={asset}
        onDismiss={onDismiss}
        onSuccess={onSuccess}
      />
    </ModalContainer>
    setActiveModals({...activeModalsToUpdate});
  };

  const promptToDelist = (asset) => {

    const modalId = "walletsPromptToDelist";

    const activeModalsToUpdate = activeModals;

    const marketplaceTransactionItem = userReduxStore.userTransactionsByPetId[asset.id];

    if (!marketplaceTransactionItem) {
      console.error("Marketplace promptToDelist request ignored. asset.id not found in userReduxStore.userTransactionsByPetId.");
      return;
    }

    const onDismiss = () => {
      const activeModalsToEdit = activeModals;
      delete activeModalsToEdit[modalId];
      setActiveModals({...activeModalsToEdit}); // use spread operator to create a new object
    };

    const onSuccess = () => {
      dispatch(setDataRefreshRequestedDate(new Date())); // trigger an App.useEffect user refresh
      onDismiss();
    };

    activeModalsToUpdate[modalId] = <ModalContainer
      title={"Delist " + asset.name}
      smallPromptFormat={true}
      hideHeaderDismissButton={true}
      onDismiss={onDismiss}
    >
      <MarketplaceDelistForm
        asset={asset}
        marketplaceTransactionItem={marketplaceTransactionItem}
        onDismiss={onDismiss}
        onSuccess={onSuccess}
      />
    </ModalContainer>
    setActiveModals({...activeModalsToUpdate});
  };

  const labelForStatus = (accountLinkStatusType) => {
    switch (accountLinkStatusType) {
      case ACCOUNT_LINK_STATUS_TYPES.LOADING:
        return "Loading...";
      case ACCOUNT_LINK_STATUS_TYPES.INCOMPLETE_PLUS_DISABLED:
        return "Incomplete";
      case ACCOUNT_LINK_STATUS_TYPES.INCOMPLETE:
        return "Incomplete";
      case ACCOUNT_LINK_STATUS_TYPES.PENDING:
        return "Pending";
      case ACCOUNT_LINK_STATUS_TYPES.COMPLETE:
        return "Complete";
      default:
        break;
    }
  }

  const iconForStatus = (accountLinkStatusType) => {
    switch (accountLinkStatusType) {
      case ACCOUNT_LINK_STATUS_TYPES.LOADING:
        return <DownloadingIcon />;
      case ACCOUNT_LINK_STATUS_TYPES.INCOMPLETE_PLUS_DISABLED:
        return <BlockIcon />;
      case ACCOUNT_LINK_STATUS_TYPES.INCOMPLETE:
        return <WarningIcon />;
      case ACCOUNT_LINK_STATUS_TYPES.PENDING:
        return <PendingIcon />;
      case ACCOUNT_LINK_STATUS_TYPES.COMPLETE:
        return <CheckIcon />;
      default:
        break;
    }
  }

  const getIdlePetParadiseLinkStatus = () => {
    let status = ACCOUNT_LINK_STATUS_TYPES.LOADING;
    if (userReduxStore.braincloudSession.sessionId) {
      status = ACCOUNT_LINK_STATUS_TYPES.COMPLETE;
    } else {
      status = ACCOUNT_LINK_STATUS_TYPES.INCOMPLETE;
    }
    return status;
  };

  const getForteLinkStatus = () => {
    let status = ACCOUNT_LINK_STATUS_TYPES.INCOMPLETE;
    if (userReduxStore.forteData.id) {
      if (userReduxStore.forteData.exchangeId) {
        status = ACCOUNT_LINK_STATUS_TYPES.COMPLETE;
      } else if (userReduxStore.forteData.exchangeLinkOrderId) {
        status = ACCOUNT_LINK_STATUS_TYPES.PENDING;
      }
    } else {
      status = ACCOUNT_LINK_STATUS_TYPES.LOADING;
    }
  
    if (status === ACCOUNT_LINK_STATUS_TYPES.LOADING) {
      if (getIdlePetParadiseLinkStatus() === ACCOUNT_LINK_STATUS_TYPES.INCOMPLETE) {
        status = ACCOUNT_LINK_STATUS_TYPES.INCOMPLETE_PLUS_DISABLED;
      }
    }
    return status;
  };

  const idlePetParadiseLinkStatus = getIdlePetParadiseLinkStatus();

  const forteLinkStatus = getForteLinkStatus();

  let forteLink = forteLinkUrl;
  if (forteLinkStatus === ACCOUNT_LINK_STATUS_TYPES.COMPLETE) {
    switch (process.env.REACT_APP_ENV) {
      case "staging":
        forteLink = "https://gamma.playerexchange.io";
        break;
      default:
        forteLink = "https://exchange.forte.io";
        break;
    }
  }

  const isSignedIn = userReduxStore.user && userReduxStore.user.attributes && userReduxStore.user.attributes.email ? true : false;

  if (!isSignedIn) {
    return (<ModalContainer
      title={"Magmic Core Sign In"}
      smallPromptFormat={true}
      noMainContentPadding={true}
      hideHeaderDismissButton={false}
      onDismiss={() => {history.push("/");}}
    >
      <div className="AmplifyWrapper">
        <AmplifyProvider theme={amplifyTheme}>
          <Authenticator loginMechanisms={['email']}></Authenticator>
        </AmplifyProvider>
      </div>
    </ModalContainer>);
  }

  return (
    <div className="wallet">
      {Object.keys(activeModals).map( (modalId) => {
        return activeModals[modalId]
      })}
      <header className="sectionLanding profile">
        <div className="sectionLandingContentWrapper">
          <div className="sectionLandingLeft profileHeader">
            <div className="headerWithActions">
              <h1>
                Profile
              </h1>
              {/*}
              <div className="actions">
                <button onClick={signOut}>Sign Out</button>
              </div>
              */}
            </div>
            {loading ?
              <div className="loadingContainer"></div>
            :
              /*<>
              <div className="profileDetails">
                <div className="name actions">
                  <span className="label">Name</span><span className="value">WIP</span><button><EditIcon /></button>
                </div>
              </div>*/
              <>
              <div className="accountLinks">
                <span className="label">Account Links</span>
                <div className="accounts">
                  <div className="service">
                    <div className="actions">
                      <button disabled={(idlePetParadiseLinkStatus === ACCOUNT_LINK_STATUS_TYPES.INCOMPLETE) || (idlePetParadiseLinkStatus === ACCOUNT_LINK_STATUS_TYPES.COMPLETE) ? false : true} onClick={promptToConnectIdlePetParadise} >Idle Pet Paradise</button>
                      <div className={"status " + idlePetParadiseLinkStatus.toLowerCase()}>
                        {labelForStatus(idlePetParadiseLinkStatus)}
                      </div>
                    </div>
                    <div className={"statusIcon " + idlePetParadiseLinkStatus.toLowerCase()}>
                      {iconForStatus(idlePetParadiseLinkStatus)}
                    </div>
                  </div>
                  <div className="service">
                    <div className="actions">
                      {(forteLinkStatus === ACCOUNT_LINK_STATUS_TYPES.INCOMPLETE) || (forteLinkStatus === ACCOUNT_LINK_STATUS_TYPES.COMPLETE) ? //TODO temporarily keeping forte exchange link active after connection completed
                        <a href={forteLink} target="_blank" onClick={() => {
                          if (!window.location.href.startsWith("http://localhost")) {
                            ReactGA.event("link_forte_account");
                          }
                        }}>Forte Exchange</a>
                      :
                        <button disabled={true} >Forte Exchange</button>
                      }
                      <div className={"status " + forteLinkStatus.toLowerCase()}>
                        {labelForStatus(forteLinkStatus)}
                      </div>
                    </div>
                    <div className={"statusIcon " + forteLinkStatus.toLowerCase()}>
                      {iconForStatus(forteLinkStatus)}
                    </div>
                  </div>
                </div>
              </div>
              {idlePetParadiseLinkStatus === ACCOUNT_LINK_STATUS_TYPES.INCOMPLETE ?
                <div className="gameLinkDetails">
                  <ul>Link your Magmic Core account to your Idle Pet Paradise game account to access:
                    <li>Magmic Coin Balance</li>
                    <li>Current NFTs</li>
                    <li>Trading NFTs in Magmic Core</li>
                  </ul>
                </div>
              :
                null
              }
              </>
              /*</>*/
            }
          </div>
            {!loading ?
              <div className="sectionLandingRight">
              {idlePetParadiseLinkStatus === ACCOUNT_LINK_STATUS_TYPES.INCOMPLETE ?
                <div className="gameLinkDetails">
                  <img alt="Idle Pet Paradise" src="https://idlepetparadise.com/wp-content/uploads/2021/11/Idle_Pet_Paradise_Logo_628x540.png" style={{width: "350px", maxWidth: "100%"}} />
                  <div className="appStoreLinks">
                    <a href="https://apps.apple.com/app/apple-store/id1590186647?pt=207581&ct=MagmicCore&mt=8" target={"_blank"}><img src="./images/app_stores/apple.png" /></a>
                    <a href="https://play.google.com/store/apps/details?id=com.magmic.android.IdlePetParadise" target={"_blank"}><img src="./images/app_stores/google.png" /></a>
                  </div>
                </div>
              :
                null // idlePetParadiseLinkStatus
              }
              {forteLinkStatus === ACCOUNT_LINK_STATUS_TYPES.INCOMPLETE ?
                <div className="forteLinkDetails">
                  <p>
                    Link your wallet to the Forte Exchange to purchase Magmic Coin
                  </p>
                </div>
              :
                null
              }
              </div>
            :
              null // loading
            }
        </div>
      </header>
      <div className="sectionSeparator sharp"></div>
      <header className="sectionLanding">
        <div className="sectionLandingContentWrapper">
          <div className="sectionLandingLeft profileHeader">
            <h1>
              Your Wallet
            </h1>
            {loading ?
              <div className="loadingContainer"></div>
            :
              <div className="profileDetails">
                <div className="walletBalance actions">
                  <span className="label">Magmic Coin</span>
                  { idlePetParadiseLinkStatus === ACCOUNT_LINK_STATUS_TYPES.COMPLETE ?
                    <>
                      <span className="value">{getMagmicCoinBalanceString(userReduxStore.magmicCoin)}</span>
                      <span className="currency">&nbsp;<img src={imageMagmicCoin} />&nbsp;</span>
                      { forteLinkStatus === ACCOUNT_LINK_STATUS_TYPES.COMPLETE ?
                        <a href={forteLink} className="buyMagmicCoin" target="_blank">Buy</a>
                      :
                        <a href={forteLink} className="buyMagmicCoin" target="_blank">Link</a>
                      }
                    </>
                  :
                    <span className="value">Link account to view balance here.</span>
                  }
                </div>
              </div>
            }
          </div>
        </div>
      </header>
      {loading ?
        <div className="sectionWrapper">
          <section className="marketplaceSection">
            <div className="marketplaceResults loadingContainer">
            </div>
          </section>
        </div>
      :
        <div className="sectionWrapper">
          <section className="marketplaceSection">
            { idlePetParadiseLinkStatus === ACCOUNT_LINK_STATUS_TYPES.COMPLETE ?
              <h1>
                Items
              </h1>
            :
              null
            }
            <div className="marketplaceResults">
              {userReduxStore.userPets.map((pet) => {
                return (
                  <div className="assetSummary" key={pet.id}>
                      <div className="assetSummarySeparatorVertical"></div>
                      <div className="assetSummaryContentWrapper">
                      <Pet pet={pet} parentComponentExtendedContent={
                        <>
                          <div className="actions">
                            {pet.rarity !== 0 ?
                              <>
                                {userReduxStore.userTransactionPetIds.includes(pet.id) ?
                                  <button className="delist" onClick={() => {
                                    promptToDelist(pet);
                                  }}>
                                    Delist
                                  </button>                                
                                :
                                  <button className="sell" onClick={() => {
                                    promptToSell(pet);
                                  }}>
                                    Sell
                                  </button>
                                }
                              </>
                            :
                              <button className="info" onClick={() => {
                                promptStarterPetsCannotBeSold(pet);
                              }}>
                                <InfoIcon />
                              </button>
                            }
                          </div>
                        </>
                      } />
                    </div>
                  </div>
                )
              })}
            </div>
          </section>
        </div>
      }
    </div>
  );
}

export default Wallets;